import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Task, FullProject, LogEntry, ProjectMetadata } from '../../types';
import { canonicalProjectId, areProjectIdsMatching } from '../../utils/idNormalizer';
import { areTaskListsEqual } from '../../utils/taskComparison';
import { useProjectPersistence } from './useProjectPersistence';
import { useProjectCRUD } from './useProjectCRUD';
import { useProjectSyncEngine } from './useProjectSyncEngine';
import { useProjectBootstrap } from './useProjectBootstrap';
import {
  pruneAbandonedInlineDraftsOnProjectSwitch,
  isAbandonedInlineDraft,
} from '../../utils/dragGuardState';
import { createProjectSyncTracker } from './sync/projectSyncStateTracker';
import {
  GLOBAL_VIEW_IDS_SET,
  normalizeAndDeduplicateProjectList,
  pruneUnnamedGhostTasks,
  computeProjectTasksWithCommVorgaenge,
  resolveActiveProjectInstance,
} from './projectStateNormalizer';

export interface UseProjectStateAndStorageProps {
  userId?: string;
  tasks: Task[];
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  setProjectName: React.Dispatch<React.SetStateAction<string>>;
  setProjectMetadata: React.Dispatch<React.SetStateAction<ProjectMetadata>>;
  setChangelog: React.Dispatch<React.SetStateAction<LogEntry[]>>;
}

export function useProjectStateAndStorage({
  userId,
  tasks,
  setTasks,
  setProjectName,
  setProjectMetadata,
  setChangelog,
}: UseProjectStateAndStorageProps) {
  const { user, tenantId: rawTenantId, userRole } = useAuth();
  const tenantId = rawTenantId || 'telfs';
  const COMM_PROJECT_ID = `comm_internal_${userId || 'unknown'}`;
  const INBOX_PROJECT_ID = userId ? `inbox_inforum_${userId}` : 'inbox_inforum';

  const normalizeProjectId = useCallback((id?: string) => {
    return id ? canonicalProjectId(id) : id;
  }, []);

  const currentProjectIdRef = useRef<string>('globalKanban');
  const [currentProjectId, setCurrentProjectId] = useState<string>('globalKanban');
  const [isProjectLoading, setIsProjectLoading] = useState(false);
  const lastLoadedIdRef = useRef<string | null>(null);
  const lastLoadedTasksIdRef = useRef<string | null>(null);

  const [allProjects, _setAllProjects] = useState<FullProject[]>([]);
  const allProjectsRef = useRef<FullProject[]>([]);

  const normalizeAndDeduplicateProjects = useCallback(
    (next: FullProject[]): FullProject[] =>
      normalizeAndDeduplicateProjectList(next, INBOX_PROJECT_ID, COMM_PROJECT_ID),
    [INBOX_PROJECT_ID, COMM_PROJECT_ID]
  );

  const setAllProjects = useCallback(
    (updater: FullProject[] | ((prev: FullProject[]) => FullProject[])) => {
      const prev = allProjectsRef.current;
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (next === prev && prev.length > 0) {
        return;
      }
      const finalProjects = normalizeAndDeduplicateProjects(next);
      allProjectsRef.current = finalProjects;
      _setAllProjects(finalProjects);
    },
    [normalizeAndDeduplicateProjects]
  );

  const [activeWorkspace, setActiveWorkspace] = useState<'professional' | 'private'>(() => {
    const saved = localStorage.getItem('gantt_active_workspace');
    return (saved === 'private' ? 'private' : 'professional') as 'professional' | 'private';
  });

  useEffect(() => {
    const prevProjectId = currentProjectIdRef.current;
    currentProjectIdRef.current = currentProjectId;
    if (prevProjectId && prevProjectId !== currentProjectId && allProjectsRef.current.length > 0) {
      setAllProjects((prev) =>
        pruneAbandonedInlineDraftsOnProjectSwitch(prev, prevProjectId, currentProjectId)
      );
    }
  }, [currentProjectId, setAllProjects]);

  const projects = useMemo(() => {
    return allProjects
      .filter((p) => {
        const projectWorkspace = p.workspace || 'professional';
        const id = p.id.toLowerCase();
        return (
          projectWorkspace === activeWorkspace &&
          !id.startsWith('inbox_inforum') &&
          !id.startsWith('comm_internal') &&
          !id.startsWith('kommunikation') &&
          !id.startsWith('posteingang')
        );
      })
      .sort((a, b) => {
        if (a.order !== undefined || b.order !== undefined) {
          const orderA = a.order ?? 9999;
          const orderB = b.order ?? 9999;
          if (orderA !== orderB) return orderA - orderB;
        }
        return (b.lastModified || 0) - (a.lastModified || 0);
      });
  }, [allProjects, activeWorkspace]);

  useEffect(() => {
    localStorage.setItem('gantt_active_workspace', activeWorkspace);
  }, [activeWorkspace]);

  const {
    isSyncing,
    setIsSyncing,
    syncError,
    setSyncError,
    lastSynced,
    setLastSynced,
    isOnline,
    pendingSyncCount,
    isInitialLoadComplete,
    dirtyProjectIdsRef,
    dirtyTaskIdsRef,
    recentlyDeletedTaskIdsRef,
    syncingProjectIdsRef,
    rawProjectsRef,
    rawTasksRef,
    ownedTasksRef,
    migratedProjectIdsRef,
    markProjectDirty,
    markTaskDirty,
    syncProjectToFirestore,
    fetchProjectsFromFirestore,
    syncInboxForce,
  } = useProjectSyncEngine({
    userId,
    user,
    tenantId,
    userRole: userRole || undefined,
    COMM_PROJECT_ID,
    INBOX_PROJECT_ID,
    normalizeProjectId,
    allProjectsRef,
    setAllProjects,
  });

  const isLoadingProjects = !isInitialLoadComplete;

  const syncTracker = useMemo(
    () => createProjectSyncTracker({
      allProjectsRef, rawProjectsRef, rawTasksRef, ownedTasksRef, dirtyTaskIdsRef, recentlyDeletedTaskIdsRef,
    }),
    [allProjectsRef, rawProjectsRef, rawTasksRef, ownedTasksRef, dirtyTaskIdsRef, recentlyDeletedTaskIdsRef]
  );

  useEffect(() => {
    if (allProjects.length === 0) return;
    const timer = setTimeout(() => {
      setAllProjects((currentProjects) => {
        const next = pruneUnnamedGhostTasks(currentProjects);
        if (next === currentProjects) return currentProjects;
        const survivingIds = new Set(next.flatMap((p) => p.tasks || []).map((t) => String(t.id)));
        if (rawTasksRef.current) rawTasksRef.current = rawTasksRef.current.filter((t) => survivingIds.has(String(t.id)));
        if (ownedTasksRef.current) ownedTasksRef.current = ownedTasksRef.current.filter((t) => survivingIds.has(String(t.id)));
        return next;
      });
    }, 1000);
    return () => clearTimeout(timer);
  }, [allProjects, setAllProjects, rawTasksRef, ownedTasksRef]);

  useEffect(() => {
    if (!isInitialLoadComplete) return;
    if (
      currentProjectId &&
      currentProjectId !== 'default' &&
      !GLOBAL_VIEW_IDS_SET.has(currentProjectId)
    ) {
      const exists =
        projects.some((p) => p.id === currentProjectId) || currentProjectId === 'inbox_inforum';
      if (!exists) {
        const firstProjId = projects[0]?.id || 'default';
        setCurrentProjectId(firstProjId);
      }
    }
  }, [activeWorkspace, projects, currentProjectId, isInitialLoadComplete]);

  useProjectBootstrap({
    userId,
    userEmail: user?.email,
    tenantId,
    COMM_PROJECT_ID,
    INBOX_PROJECT_ID,
    activeWorkspace,
    allProjectsRef,
    currentProjectIdRef,
    setAllProjects,
    setCurrentProjectId,
    setLastSynced,
    syncProjectToFirestore,
    fetchProjectsFromFirestore,
  });

  const getProjectTasksWithCommVorgaenge = useCallback(
    (proj: FullProject, projectsList: FullProject[]): Task[] =>
      computeProjectTasksWithCommVorgaenge(proj, projectsList),
    []
  );

  const activeProject = useMemo(
    () =>
      resolveActiveProjectInstance({
        currentProjectId,
        allProjects,
        projects,
        INBOX_PROJECT_ID,
        COMM_PROJECT_ID,
        getProjectTasksWithCommVorgaenge,
      }),
    [
      allProjects,
      projects,
      currentProjectId,
      INBOX_PROJECT_ID,
      COMM_PROJECT_ID,
      getProjectTasksWithCommVorgaenge,
    ]
  );

  useEffect(() => {
    if (!activeProject) return;
    if (activeProject.id === lastLoadedIdRef.current) return;

    setIsProjectLoading(true);
    setTasks(activeProject.tasks || []);
    setProjectName(activeProject.name || '');
    setProjectMetadata(activeProject.metadata || {});
    setChangelog(activeProject.changelog || []);

    if (activeProject.lastModified) {
      setLastSynced((prev) => Math.max(prev || 0, activeProject.lastModified || 0));
    }

    lastLoadedIdRef.current = activeProject.id;
    lastLoadedTasksIdRef.current = activeProject.id;
    const timer = setTimeout(() => setIsProjectLoading(false), 200);
    return () => clearTimeout(timer);
  }, [
    activeProject?.id,
    activeProject,
    setChangelog,
    setIsProjectLoading,
    setLastSynced,
    setProjectMetadata,
    setProjectName,
    setTasks,
  ]);

  useEffect(() => {
    const proj = allProjects.find(
      (p) =>
        p.id === currentProjectId ||
        (currentProjectId === 'inbox_inforum' &&
          (p.id === INBOX_PROJECT_ID || p.id.toLowerCase().startsWith('inbox_inforum_'))) ||
        (currentProjectId === 'comm_internal' &&
          (p.id === COMM_PROJECT_ID || p.id.toLowerCase().startsWith('comm_internal_')))
    );
    if (!proj) return;

    const authoritativeTasks = getProjectTasksWithCommVorgaenge(proj, allProjects);
    setTasks((prev) => {
      const isSameLoadedProject = lastLoadedIdRef.current === proj.id;
      const hasInlineNew =
        isSameLoadedProject &&
        prev.some(
          (t) =>
            t.isInlineNew &&
            (!t.projectId || areProjectIdsMatching(t.projectId, proj.id)) &&
            !isAbandonedInlineDraft(t)
        );
      if (hasInlineNew) {
        const mergedWithInline = authoritativeTasks.map((ct) => {
          const localMatch = prev.find((pt) => String(pt.id) === String(ct.id));
          return localMatch && localMatch.isInlineNew && !isAbandonedInlineDraft(localMatch)
            ? localMatch
            : ct;
        });
        const newInlineTasks = prev.filter(
          (pt) =>
            pt.isInlineNew &&
            (!pt.projectId || areProjectIdsMatching(pt.projectId, proj.id)) &&
            !isAbandonedInlineDraft(pt) &&
            !authoritativeTasks.some((ct) => String(ct.id) === String(pt.id))
        );
        return [...mergedWithInline, ...newInlineTasks];
      }

      if (areTaskListsEqual(authoritativeTasks, prev)) {
        return prev;
      }
      return authoritativeTasks;
    });

    setProjectName((prev) => (prev === (proj.name || '') ? prev : proj.name || ''));
    setProjectMetadata((prev) => {
      const newMeta = proj.metadata || {};
      if (JSON.stringify(prev) === JSON.stringify(newMeta)) return prev;
      return newMeta;
    });
    setChangelog((prev) => {
      const newChangelog = proj.changelog || [];
      if (prev.length === newChangelog.length && JSON.stringify(prev) === JSON.stringify(newChangelog))
        return prev;
      return newChangelog;
    });
  }, [
    allProjects,
    currentProjectId,
    INBOX_PROJECT_ID,
    COMM_PROJECT_ID,
    getProjectTasksWithCommVorgaenge,
    setChangelog,
    setProjectMetadata,
    setProjectName,
    setTasks,
  ]);

  const { restoreProjectVersion, deleteProjectFromPersistence } = useProjectPersistence({
    userId: userId || null,
    tenantId,
    COMM_PROJECT_ID,
    INBOX_PROJECT_ID,
    currentProjectId,
    setTasks,
    setProjectName,
    setProjectMetadata,
    setChangelog,
    setAllProjects,
    syncProjectToFirestore,
  });

  const {
    reorderProjects,
    addProject,
    archiveProject,
    unarchiveProject,
    confirmDuplicateProject,
    deleteProject,
    renameProject,
  } = useProjectCRUD({
    allProjects,
    setAllProjects,
    currentProjectId,
    setCurrentProjectId,
    setProjectName,
    activeWorkspace,
    userId: userId || null,
    tenantId,
    COMM_PROJECT_ID,
    INBOX_PROJECT_ID,
    markProjectDirty,
    syncProjectToFirestore,
    deleteProjectFromPersistence,
  });

  return {
    allProjects,
    setAllProjects,
    currentProjectId,
    setCurrentProjectId,
    activeProject,
    projects,
    activeWorkspace,
    setActiveWorkspace,
    isSyncing,
    setIsSyncing,
    syncError,
    setSyncError,
    lastSynced,
    setLastSynced,
    isOnline,
    pendingSyncCount,
    isInitialLoadComplete,
    isLoadingProjects,
    isProjectLoading,

    user,
    tenantId,
    COMM_PROJECT_ID,
    INBOX_PROJECT_ID,
    normalizeProjectId,

    syncTracker,
    allProjectsRef,
    currentProjectIdRef,
    rawProjectsRef,
    rawTasksRef,
    ownedTasksRef,
    migratedProjectIdsRef,
    dirtyProjectIdsRef,
    dirtyTaskIdsRef,
    recentlyDeletedTaskIdsRef,
    syncingProjectIdsRef,
    lastLoadedIdRef,
    lastLoadedTasksIdRef,

    markProjectDirty,
    markTaskDirty,
    syncProjectToFirestore,
    deleteProjectFromPersistence,
    syncInboxForce,
    restoreProjectVersion,

    reorderProjects,
    addProject,
    archiveProject,
    unarchiveProject,
    confirmDuplicateProject,
    deleteProject,
    renameProject,
    getProjectTasksWithCommVorgaenge,
  };
}
